import React, { useState, useEffect } from 'react';
import { User, Mic, Guitar, Music } from 'lucide-react';
import { API_URL, fetchWithAuth } from '../services/ApiService';

/**
 * Component for assigning roles to a sheet within an event's setlist
 */
const SheetRoleAssignment = ({ 
  sheet, 
  eventId, 
  setlistId, 
  existingRoles = null,
  onUpdate,
  isCompact = false 
}) => {
  const [roles, setRoles] = useState({
    leadVocal: '',
    backingVocals: [],
    guitar: '',
    bass: '',
    drums: '',
    keys: '',
    saxophone: '',
    trumpet: '',
    other: []
  });
  
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [performanceNotes, setPerformanceNotes] = useState('');
  const [showMentions, setShowMentions] = useState(false);
  const [mentionField, setMentionField] = useState(null);
  const [mentionSearch, setMentionSearch] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  
  // Load existing roles
  useEffect(() => {
    if (existingRoles) {
      setRoles(existingRoles.roles || {
        leadVocal: '',
        backingVocals: [],
        guitar: '',
        bass: '',
        drums: '',
        keys: '',
        saxophone: '',
        trumpet: '',
        other: []
      });
      setPerformanceNotes(existingRoles.performanceNotes || '');
    }
  }, [existingRoles]);
  
  // Search users for mentions
  useEffect(() => {
    const searchUsers = async () => {
      if (!mentionSearch || mentionSearch.length < 1) {
        setSuggestions([]);
        return;
      }

      try {
        const response = await fetch(
          `${API_URL}/users/search?q=${encodeURIComponent(mentionSearch)}&limit=10`,
          {
            credentials: 'include'
          }
        );
        
        if (response.ok) {
          const users = await response.json();
          setSuggestions(users || []);
        }
      } catch (err) {
        console.error('Error searching users:', err);
        setSuggestions([]);
      }
    };

    const debounceTimer = setTimeout(searchUsers, 300);
    return () => clearTimeout(debounceTimer);
  }, [mentionSearch]);
  
  const handleRoleChange = (roleField, value) => {
    if (roleField === 'backingVocals') {
      // Handle array field
      setRoles(prev => ({
        ...prev,
        backingVocals: value.split(',').map(v => v.trim()).filter(v => v)
      }));
    } else {
      setRoles(prev => ({
        ...prev,
        [roleField]: value
      }));
    }
    
    // Check for @mention
    if (value.endsWith('@')) {
      setMentionField(roleField);
      setMentionSearch('');
      setShowMentions(true);
    } else if (mentionField === roleField && value.includes('@')) {
      const lastAtIndex = value.lastIndexOf('@');
      const searchText = value.substring(lastAtIndex + 1);
      setMentionSearch(searchText);
    } else {
      setShowMentions(false);
      setMentionField(null);
    }
  };
  
  const insertMention = (user, roleField) => {
    const currentValue = roleField === 'backingVocals' 
      ? roles.backingVocals.join(', ')
      : roles[roleField] || '';
    
    const lastAtIndex = currentValue.lastIndexOf('@');
    const beforeMention = lastAtIndex >= 0 ? currentValue.substring(0, lastAtIndex) : currentValue;
    const newValue = `${beforeMention}@${user.username}`;
    
    handleRoleChange(roleField, newValue);
    setShowMentions(false);
    setMentionField(null);
    setMentionSearch('');
  };
  
  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/events/${eventId}/setlist/${setlistId}/sheet/${sheet._id}/roles`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            roles,
            performanceNotes
          })
        }
      );
      
      if (response.success) {
        setIsEditing(false);
        if (onUpdate) {
          onUpdate(response.data);
        }
      } else {
        alert('Failed to save role assignments');
      }
    } catch (error) {
      console.error('Error saving roles:', error);
      alert('Failed to save role assignments');
    } finally {
      setSaving(false);
    }
  };
  
  const handleCancel = () => {
    // Reset to original values
    if (existingRoles) {
      setRoles(existingRoles.roles || {
        leadVocal: '',
        backingVocals: [],
        guitar: '',
        bass: '',
        drums: '',
        keys: '',
        saxophone: '',
        trumpet: '',
        other: []
      });
      setPerformanceNotes(existingRoles.performanceNotes || '');
    }
    setIsEditing(false);
  };
  
  // Get effective lead vocalist (from roles or sheet default)
  const getEffectiveLeadVocalist = () => {
    return roles.leadVocal || sheet.leadVocalist || '';
  };
  
  if (isCompact && !isEditing) {
    // Compact view for setlist display
    const leadVocalist = getEffectiveLeadVocalist();
    const hasRoleAssignments = Object.values(roles).some(v => 
      Array.isArray(v) ? v.length > 0 : v
    );
    
    return (
      <div className="flex items-center gap-2 text-sm">
        {leadVocalist && (
          <div className="flex items-center gap-1">
            <Mic className="w-3 h-3 text-muted-foreground" />
            <span className="text-xs">{leadVocalist}</span>
          </div>
        )}
        {roles.guitar && (
          <div className="flex items-center gap-1">
            <Guitar className="w-3 h-3 text-muted-foreground" />
            <span className="text-xs">{roles.guitar}</span>
          </div>
        )}
        {(hasRoleAssignments || sheet.leadVocalist) && (
          <button
            onClick={() => setIsEditing(true)}
            className="text-xs text-primary hover:underline ml-2"
          >
            Edit roles
          </button>
        )}
        {!hasRoleAssignments && !sheet.leadVocalist && (
          <button
            onClick={() => setIsEditing(true)}
            className="text-xs text-muted-foreground hover:text-primary"
          >
            + Assign roles
          </button>
        )}
      </div>
    );
  }
  
  if (!isEditing) {
    // Full view (non-editing)
    return (
      <div className="p-4 border rounded-md bg-card">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium flex items-center gap-2">
            <Music className="w-4 h-4" />
            {sheet.title}
            {sheet.artist && <span className="text-sm text-muted-foreground">by {sheet.artist}</span>}
          </h4>
          <button
            onClick={() => setIsEditing(true)}
            className="text-sm text-primary hover:underline"
          >
            Edit roles
          </button>
        </div>
        
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div>
            <span className="text-muted-foreground">Lead Vocal:</span>{' '}
            <span className={roles.leadVocal ? 'font-medium' : 'text-muted-foreground'}>
              {getEffectiveLeadVocalist() || 'Not assigned'}
              {!roles.leadVocal && sheet.leadVocalist && ' (default)'}
            </span>
          </div>
          
          {roles.backingVocals?.length > 0 && (
            <div>
              <span className="text-muted-foreground">Backing Vocals:</span>{' '}
              {roles.backingVocals.join(', ')}
            </div>
          )}
          
          {roles.guitar && (
            <div>
              <span className="text-muted-foreground">Guitar:</span> {roles.guitar}
            </div>
          )}
          
          {roles.bass && (
            <div>
              <span className="text-muted-foreground">Bass:</span> {roles.bass}
            </div>
          )}
          
          {roles.drums && (
            <div>
              <span className="text-muted-foreground">Drums:</span> {roles.drums}
            </div>
          )}
          
          {roles.keys && (
            <div>
              <span className="text-muted-foreground">Keys:</span> {roles.keys}
            </div>
          )}
        </div>
        
        {performanceNotes && (
          <div className="mt-3 pt-3 border-t border-border">
            <p className="text-sm text-muted-foreground">{performanceNotes}</p>
          </div>
        )}
      </div>
    );
  }
  
  // Editing view
  return (
    <div className="p-4 border rounded-md bg-card">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-medium flex items-center gap-2">
          <Music className="w-4 h-4" />
          {sheet.title}
          {sheet.artist && <span className="text-sm text-muted-foreground">by {sheet.artist}</span>}
        </h4>
      </div>
      
      <div className="space-y-3">
        {/* Lead Vocal */}
        <div>
          <label className="block text-sm font-medium mb-1">
            Lead Vocal
            {sheet.leadVocalist && (
              <span className="text-muted-foreground font-normal ml-2">
                (default: {sheet.leadVocalist})
              </span>
            )}
          </label>
          <input
            type="text"
            value={roles.leadVocal || ''}
            onChange={(e) => handleRoleChange('leadVocal', e.target.value)}
            placeholder={sheet.leadVocalist || '@mention a user'}
            className="w-full px-3 py-1 text-sm border rounded-md"
          />
        </div>
        
        {/* Backing Vocals */}
        <div>
          <label className="block text-sm font-medium mb-1">Backing Vocals</label>
          <input
            type="text"
            value={roles.backingVocals?.join(', ') || ''}
            onChange={(e) => handleRoleChange('backingVocals', e.target.value)}
            placeholder="@mention users, separated by commas"
            className="w-full px-3 py-1 text-sm border rounded-md"
          />
        </div>
        
        {/* Instruments */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium mb-1">Guitar</label>
            <input
              type="text"
              value={roles.guitar || ''}
              onChange={(e) => handleRoleChange('guitar', e.target.value)}
              placeholder="@mention"
              className="w-full px-3 py-1 text-sm border rounded-md"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Bass</label>
            <input
              type="text"
              value={roles.bass || ''}
              onChange={(e) => handleRoleChange('bass', e.target.value)}
              placeholder="@mention"
              className="w-full px-3 py-1 text-sm border rounded-md"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Drums</label>
            <input
              type="text"
              value={roles.drums || ''}
              onChange={(e) => handleRoleChange('drums', e.target.value)}
              placeholder="@mention"
              className="w-full px-3 py-1 text-sm border rounded-md"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium mb-1">Keys</label>
            <input
              type="text"
              value={roles.keys || ''}
              onChange={(e) => handleRoleChange('keys', e.target.value)}
              placeholder="@mention"
              className="w-full px-3 py-1 text-sm border rounded-md"
            />
          </div>
        </div>
        
        {/* Performance Notes */}
        <div>
          <label className="block text-sm font-medium mb-1">Performance Notes</label>
          <textarea
            value={performanceNotes}
            onChange={(e) => setPerformanceNotes(e.target.value)}
            placeholder="Any specific notes for this performance..."
            className="w-full px-3 py-1 text-sm border rounded-md"
            rows={2}
          />
        </div>
        
        {/* Action buttons */}
        <div className="flex justify-end gap-2">
          <button
            onClick={handleCancel}
            className="px-3 py-1 text-sm border rounded-md hover:bg-muted"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-3 py-1 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Roles'}
          </button>
        </div>
      </div>
      
      {/* Mention suggestions dropdown */}
      {showMentions && suggestions.length > 0 && mentionField && (
        <div className="absolute z-50 mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto">
          {suggestions.map((user) => (
            <div
              key={user._id}
              className="px-3 py-2 cursor-pointer hover:bg-gray-50 flex items-center gap-2"
              onClick={() => insertMention(user, mentionField)}
            >
              <User className="w-4 h-4" />
              <div>
                <div className="font-medium">{user.username || user.email}</div>
                {user.firstName || user.lastName ? (
                  <div className="text-sm text-gray-500">
                    {user.firstName} {user.lastName}
                  </div>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SheetRoleAssignment;